/**
 * Pure state machine for ActiveMeshFreezer. The Babylon-facing driver lives
 * in activeMeshFreezer.ts; keeping time and scene calls out of this module
 * makes every debounce, retry, and suspension edge deterministic in tests.
 */

export const ACTIVE_MESH_QUIET_MS = 1000;
export const ACTIVE_MESH_MAX_FREEZE_ERRORS = 3;

export type ActiveMeshFreezerStatus =
  | 'scheduled'
  | 'freezing'
  | 'frozen'
  | 'suspended'
  | 'unfrozen';

export type ActiveMeshFreezerCommand = 'freeze' | 'unfreeze';

export interface ActiveMeshFreezerModel {
  status: ActiveMeshFreezerStatus;
  deadlineMs: number | null;
  suspendReasons: ReadonlyMap<string, number>;
  consecutiveFreezeErrors: number;
  totalFreezeErrors: number;
  freezeCount: number;
  lastChurnReason: string | null;
}

export type ActiveMeshFreezerEvent =
  | { type: 'CHURN'; now: number; reason: string }
  | { type: 'TICK'; now: number }
  | { type: 'FREEZE_OK' }
  | { type: 'FREEZE_ERR'; now: number }
  | { type: 'SUSPEND'; reason: string }
  | { type: 'RESUME'; now: number; reason: string };

export interface ActiveMeshFreezerTransition {
  state: ActiveMeshFreezerModel;
  commands: ActiveMeshFreezerCommand[];
}

/** Create the initial one-second quiet window after attachment. */
export function createActiveMeshFreezerState(now: number): ActiveMeshFreezerModel {
  return {
    status: 'scheduled',
    deadlineMs: now + ACTIVE_MESH_QUIET_MS,
    suspendReasons: new Map(),
    consecutiveFreezeErrors: 0,
    totalFreezeErrors: 0,
    freezeCount: 0,
    lastChurnReason: null,
  };
}

/** Apply one event without mutating the previous model. */
export function transitionActiveMeshFreezer(
  current: ActiveMeshFreezerModel,
  event: ActiveMeshFreezerEvent,
): ActiveMeshFreezerTransition {
  switch (event.type) {
    case 'CHURN': {
      const commands: ActiveMeshFreezerCommand[] =
        current.status === 'frozen' || current.status === 'freezing' ? ['unfreeze'] : [];
      if (current.status === 'suspended') {
        return {
          state: { ...current, lastChurnReason: event.reason },
          commands,
        };
      }
      return {
        state: {
          ...current,
          status: 'scheduled',
          deadlineMs: event.now + ACTIVE_MESH_QUIET_MS,
          consecutiveFreezeErrors:
            current.status === 'unfrozen' ? 0 : current.consecutiveFreezeErrors,
          lastChurnReason: event.reason,
        },
        commands,
      };
    }

    case 'TICK':
      if (
        current.status !== 'scheduled' ||
        current.deadlineMs === null ||
        event.now < current.deadlineMs
      ) {
        return { state: current, commands: [] };
      }
      return {
        state: { ...current, status: 'freezing', deadlineMs: null },
        commands: ['freeze'],
      };

    case 'FREEZE_OK':
      if (current.status !== 'freezing') return { state: current, commands: [] };
      return {
        state: {
          ...current,
          status: 'frozen',
          deadlineMs: null,
          consecutiveFreezeErrors: 0,
          freezeCount: current.freezeCount + 1,
        },
        commands: [],
      };

    case 'FREEZE_ERR': {
      if (current.status !== 'freezing') return { state: current, commands: [] };
      const failures = current.consecutiveFreezeErrors + 1;
      const gaveUp = failures >= ACTIVE_MESH_MAX_FREEZE_ERRORS;
      return {
        state: {
          ...current,
          status: gaveUp ? 'unfrozen' : 'scheduled',
          deadlineMs: gaveUp ? null : event.now + ACTIVE_MESH_QUIET_MS,
          consecutiveFreezeErrors: failures,
          totalFreezeErrors: current.totalFreezeErrors + 1,
        },
        commands: ['unfreeze'],
      };
    }

    case 'SUSPEND': {
      const reasons = new Map(current.suspendReasons);
      reasons.set(event.reason, (reasons.get(event.reason) ?? 0) + 1);
      return {
        state: {
          ...current,
          status: 'suspended',
          deadlineMs: null,
          suspendReasons: reasons,
        },
        commands: current.status === 'frozen' || current.status === 'freezing' ? ['unfreeze'] : [],
      };
    }

    case 'RESUME': {
      const count = current.suspendReasons.get(event.reason);
      if (!count) return { state: current, commands: [] };
      const reasons = new Map(current.suspendReasons);
      if (count === 1) reasons.delete(event.reason);
      else reasons.set(event.reason, count - 1);
      if (reasons.size > 0) {
        return {
          state: { ...current, status: 'suspended', suspendReasons: reasons },
          commands: [],
        };
      }
      return {
        state: {
          ...current,
          status: 'scheduled',
          deadlineMs: event.now + ACTIVE_MESH_QUIET_MS,
          suspendReasons: reasons,
        },
        commands: [],
      };
    }
  }
}
